import { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, FloppyDisk, Calendar, Warning } from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import EloOrderEditor from "@/components/admin/EloOrderEditor";
import { promptSuperPassword } from "@/components/admin/SuperPasswordDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPlayersTab({ players, playerCategories, fetchData }) {
  const [playerForm, setPlayerForm] = useState({
    name: "", age: "", nickname: "", playing_style: "", cue_brand: "",
    photo_url: "", is_external: false, club_name: "", handicap: "",
    mobile_no: "", its_number: "", tier: "challenger"
  });
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [playerDialogOpen, setPlayerDialogOpen] = useState(false);
  const [availabilityPlayerId, setAvailabilityPlayerId] = useState("");
  const [unavailableDates, setUnavailableDates] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null); // { ids: [], isBulk: bool }

  const resetPlayerForm = () => setPlayerForm({
    name: "", age: "", nickname: "", playing_style: "", cue_brand: "",
    photo_url: "", is_external: false, club_name: "", handicap: "",
    mobile_no: "", its_number: "", tier: "challenger"
  });

  const handlePlayerSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...playerForm,
        age: playerForm.age ? parseInt(playerForm.age) : null,
        handicap: playerForm.handicap === "" ? 0 : parseInt(playerForm.handicap) || 0,
        is_external: playerForm.is_external,
        club_name: playerForm.is_external ? playerForm.club_name : null,
        mobile_no: playerForm.mobile_no || null,
        its_number: playerForm.its_number || null,
        tier: playerForm.tier || "challenger"
      };
      if (editingPlayer) {
        await axios.put(`${API}/players/${editingPlayer.id}`, data, { withCredentials: true });
        toast.success("Player updated");
      } else {
        await axios.post(`${API}/players`, data, { withCredentials: true });
        toast.success("Player created");
      }
      setPlayerDialogOpen(false);
      setEditingPlayer(null);
      resetPlayerForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save player");
    }
  };

  const handleEditPlayer = (player) => {
    setEditingPlayer(player);
    setPlayerForm({
      name: player.name || "", age: player.age?.toString() || "",
      nickname: player.nickname || "", playing_style: player.playing_style || "",
      cue_brand: player.cue_brand || "", photo_url: player.photo_url || "",
      is_external: player.is_external || false, club_name: player.club_name || "",
      handicap: player.handicap?.toString() || "",
      mobile_no: player.mobile_no || "", its_number: player.its_number || "",
      tier: player.tier || "challenger"
    });
    setPlayerDialogOpen(true);
  };

  const handleDeletePlayer = (playerId) => {
    setConfirmDelete({ ids: [playerId], isBulk: false });
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    setConfirmDelete({ ids: selectedIds, isBulk: true });
  };

  const executeDelete = async () => {
    if (!confirmDelete) return;
    const { ids, isBulk } = confirmDelete;
    setConfirmDelete(null);
    const pw = await promptSuperPassword({
      title: isBulk ? `Delete ${ids.length} players?` : "Delete this player?",
      description: "This permanently removes the player(s) and their fees, payouts, ELO history, match points, placements and claim records.",
      actionLabel: isBulk ? "Delete All" : "Delete Player",
    });
    if (!pw) return;
    try {
      const cfg = { headers: { "X-Super-Password": pw }, withCredentials: true };
      if (isBulk) {
        const res = await axios.post(`${API}/players/bulk-delete`, { player_ids: ids }, cfg);
        toast.success(`Deleted ${res.data.deleted} player${res.data.deleted === 1 ? "" : "s"}`);
        setSelectedIds([]);
      } else {
        await axios.delete(`${API}/players/${ids[0]}`, cfg);
        toast.success("Player deleted");
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const toggleSelect = (playerId) => {
    setSelectedIds(prev =>
      prev.includes(playerId) ? prev.filter(id => id !== playerId) : [...prev, playerId]
    );
  };

  const visiblePlayerIds = useMemo(() => players.map(p => p.id), [players]);
  const allSelected = visiblePlayerIds.length > 0 && visiblePlayerIds.every(id => selectedIds.includes(id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected || someSelected) {
      setSelectedIds(prev => prev.filter(id => !visiblePlayerIds.includes(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...visiblePlayerIds])));
    }
  };

  const selectedNames = useMemo(
    () => players.filter(p => selectedIds.includes(p.id)).map(p => p.name),
    [players, selectedIds]
  );

  const handlePhotoUpload = async (playerId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/players/${playerId}/photo`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Photo uploaded!");
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || "Upload failed"); }
  };

  const handleSaveAvailability = async () => {
    if (!availabilityPlayerId) return;
    try {
      const dates = unavailableDates.split(",").map(d => d.trim()).filter(Boolean);
      await axios.put(`${API}/players/${availabilityPlayerId}/availability`, { unavailable_dates: dates }, { withCredentials: true });
      toast.success("Availability updated!");
      setAvailabilityPlayerId("");
      setUnavailableDates("");
      fetchData();
    } catch { toast.error("Failed to update availability"); }
  };

  const internalPlayers = players.filter(p => !p.is_external);
  const externalPlayers = players.filter(p => p.is_external);

  return (
    <div className="space-y-6">
    <Card className="bg-card border-border">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle data-testid="players-count">Players ({internalPlayers.length} internal, {externalPlayers.length} external)</CardTitle>
        <div className="flex items-center gap-2">
          {selectedIds.length > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleBulkDelete}
              data-testid="bulk-delete-players-btn"
            >
              <Trash className="w-4 h-4 mr-2" />Delete Selected ({selectedIds.length})
            </Button>
          )}
          <Dialog open={playerDialogOpen} onOpenChange={setPlayerDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditingPlayer(null); resetPlayerForm(); }} data-testid="add-player-btn">
              <Plus className="w-4 h-4 mr-2" />Add Player
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader><DialogTitle>{editingPlayer ? "Edit" : "Add"} Player</DialogTitle></DialogHeader>
            <form onSubmit={handlePlayerSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Name *</Label><Input value={playerForm.name} onChange={e => setPlayerForm({ ...playerForm, name: e.target.value })} required className="bg-background border-border" data-testid="player-name-input" /></div>
                <div className="space-y-2"><Label>Age</Label><Input type="number" value={playerForm.age} onChange={e => setPlayerForm({ ...playerForm, age: e.target.value })} className="bg-background border-border" /></div>
                <div className="space-y-2"><Label>Mobile No</Label><Input value={playerForm.mobile_no} onChange={e => setPlayerForm({ ...playerForm, mobile_no: e.target.value })} placeholder="+255..." className="bg-background border-border" data-testid="player-mobile-input" /></div>
                <div className="space-y-2"><Label>ITS Number</Label><Input value={playerForm.its_number} onChange={e => setPlayerForm({ ...playerForm, its_number: e.target.value })} placeholder="ITS ID" className="bg-background border-border" data-testid="player-its-input" /></div>
                <div className="space-y-2"><Label>Nickname</Label><Input value={playerForm.nickname} onChange={e => setPlayerForm({ ...playerForm, nickname: e.target.value })} className="bg-background border-border" /></div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={playerForm.tier} onValueChange={v => setPlayerForm({ ...playerForm, tier: v })}>
                    <SelectTrigger className="bg-background border-border" data-testid="player-tier-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {playerCategories.length > 0 ? playerCategories.map(c => (
                        <SelectItem key={c.name} value={c.name}>{c.label}</SelectItem>
                      )) : (
                        <>
                          <SelectItem value="premier">Premier</SelectItem>
                          <SelectItem value="challenger">Challenger</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Playing Style</Label><Input value={playerForm.playing_style} onChange={e => setPlayerForm({ ...playerForm, playing_style: e.target.value })} className="bg-background border-border" /></div>
                <div className="space-y-2">
                  <Label>Handicap</Label>
                  <Input type="number" value={playerForm.handicap} onChange={e => setPlayerForm({ ...playerForm, handicap: e.target.value })} placeholder="0" className="bg-background border-border" data-testid="player-handicap-input" />
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/20 border border-border">
                <Switch checked={playerForm.is_external} onCheckedChange={v => setPlayerForm({ ...playerForm, is_external: v })} data-testid="player-external-toggle" />
                <div>
                  <Label className="font-medium">External Player</Label>
                  <p className="text-xs text-muted-foreground">From another club. Stats tracked but excluded from rankings.</p>
                </div>
              </div>
              {playerForm.is_external && (
                <div className="space-y-2">
                  <Label>Club Name *</Label>
                  <Input value={playerForm.club_name} onChange={e => setPlayerForm({ ...playerForm, club_name: e.target.value })} placeholder="e.g. Dar es Salaam Snooker Club" className="bg-background border-border" data-testid="player-club-input" required />
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setPlayerDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-player-btn"><FloppyDisk className="w-4 h-4 mr-2" />Save</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border">
                <TableHead className="w-10">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleSelectAll}
                    aria-label="Select all players"
                    data-testid="select-all-players-checkbox"
                    className={someSelected ? "data-[state=unchecked]:bg-primary/40" : ""}
                  />
                </TableHead>
                <TableHead>#</TableHead><TableHead>Name</TableHead><TableHead>Type</TableHead>
                <TableHead>Mobile</TableHead><TableHead>ITS</TableHead><TableHead>Points</TableHead>
                <TableHead>HC</TableHead><TableHead>Photo</TableHead><TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {players.map(player => (
                <TableRow
                  key={player.id}
                  className={`border-border ${selectedIds.includes(player.id) ? "bg-destructive/5" : ""}`}
                  data-testid={`player-row-${player.id}`}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.includes(player.id)}
                      onCheckedChange={() => toggleSelect(player.id)}
                      aria-label={`Select ${player.name}`}
                      data-testid={`select-player-${player.id}`}
                    />
                  </TableCell>
                  <TableCell>{player.is_external ? "-" : player.ranking}</TableCell>
                  <TableCell>
                    <div>
                      <span className="font-medium">{player.name}</span>
                      {player.is_external && player.club_name && <p className="text-xs text-chart-3">{player.club_name}</p>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {player.is_external ? (
                      <Badge variant="outline" className="border-chart-3/30 text-chart-3 text-xs">External</Badge>
                    ) : (
                      <Badge variant="outline" className={player.tier === "premier" ? "border-secondary/30 text-secondary text-xs" : "text-xs"}>
                        {playerCategories.find(c => c.name === player.tier)?.label || (player.tier === "premier" ? "Premier" : "Challenger")}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{player.mobile_no || "-"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{player.its_number || "-"}</TableCell>
                  <TableCell className="text-primary font-bold">{player.is_external ? "-" : player.total_points}</TableCell>
                  <TableCell>{player.handicap || 0}</TableCell>
                  <TableCell>
                    <Input type="file" accept="image/*" className="w-28 h-8 text-xs" onChange={e => e.target.files[0] && handlePhotoUpload(player.id, e.target.files[0])} />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleEditPlayer(player)}><PencilSimple className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeletePlayer(player.id)} data-testid={`delete-player-${player.id}`}><Trash className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="mt-6 p-4 rounded-lg bg-muted/20 border border-border">
          <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><Calendar className="w-4 h-4" /> Player Availability</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Select value={availabilityPlayerId} onValueChange={v => {
              setAvailabilityPlayerId(v);
              const p = players.find(x => x.id === v);
              setUnavailableDates(p?.unavailable_dates?.join(", ") || "");
            }}>
              <SelectTrigger className="bg-background border-border"><SelectValue placeholder="Select player" /></SelectTrigger>
              <SelectContent>{players.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Unavailable dates (YYYY-MM-DD, comma separated)" value={unavailableDates} onChange={e => setUnavailableDates(e.target.value)} className="bg-background border-border md:col-span-1" />
            <Button onClick={handleSaveAvailability} disabled={!availabilityPlayerId}><FloppyDisk className="w-4 h-4 mr-2" />Save Availability</Button>
          </div>
        </div>
      </CardContent>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="confirm-delete-players-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Warning className="w-5 h-5 text-destructive" weight="fill" />
              {confirmDelete?.isBulk
                ? `Delete ${confirmDelete.ids.length} player${confirmDelete.ids.length === 1 ? "" : "s"}?`
                : "Delete this player?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action <strong>cannot be undone</strong>. It will permanently remove:
              <ul className="list-disc list-inside mt-2 text-xs space-y-0.5">
                <li>Player profile{confirmDelete?.ids.length === 1 ? "" : "s"}</li>
                <li>Tournament placements, ELO history, match points</li>
                <li>Entry fees, payouts, notifications, claim requests</li>
              </ul>
              {confirmDelete?.isBulk && selectedNames.length > 0 && (
                <div className="mt-3 max-h-32 overflow-y-auto bg-muted/30 rounded-md p-2 text-xs">
                  <strong>Players:</strong> {selectedNames.join(", ")}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={executeDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-confirm-btn"
            >
              <Trash className="w-4 h-4 mr-2" />Yes, delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
    <EloOrderEditor players={players} fetchData={fetchData} />
    </div>
  );
}
